import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add, People, Business, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS } from '../../../styles/theme';
import { formatDateISO } from '../../../utils/dateUtils';
import type { AdminDialogProps } from '../types/adminTools.types';
import type {
  OrganizationsResponse,
  OrganizationUsersResponse,
  Organization,
  OrganizationUser,
  UnassignedUser,
} from '../../../types/api';

interface RoleDefinition {
  value: string;
  label: string;
  description: string;
}

interface OrgManagementDialogProps extends AdminDialogProps {
  // Organizations
  orgsData: OrganizationsResponse | null;
  orgsLoading: boolean;
  orgsError: string;
  // Unassigned users
  unassignedUsers: UnassignedUser[];
  unassignedUsersLoading: boolean;
  unassignedUsersError: string;
  // Per-org users (expanded row)
  expandedOrgId: string | null;
  orgUsersQueryData: OrganizationUsersResponse | null;
  orgUsersLoading: boolean;
  orgUsersError: string;
  // Assignment state
  assigningUserId: string | null;
  selectedOrgForAssignment: { [userId: string]: string };
  selectedRolesForAssignment: { [userId: string]: string[] };
  roleDefinitions: RoleDefinition[];
  // Callbacks
  onSelectOrgForAssignment: (next: { [userId: string]: string }) => void;
  onToggleRoleForAssignment: (userId: string, role: string) => void;
  onAssignUserToOrg: (userId: string) => Promise<void>;
  onToggleOrgExpand: (orgId: string) => void;
  onOpenCreateOrg: () => void;
}

export const OrgManagementDialog: React.FC<OrgManagementDialogProps> = ({
  open,
  onClose,
  orgsData,
  orgsLoading,
  orgsError,
  unassignedUsers,
  unassignedUsersLoading,
  unassignedUsersError,
  expandedOrgId,
  orgUsersQueryData,
  orgUsersLoading,
  orgUsersError,
  assigningUserId,
  selectedOrgForAssignment,
  selectedRolesForAssignment,
  roleDefinitions,
  onSelectOrgForAssignment,
  onToggleRoleForAssignment,
  onAssignUserToOrg,
  onToggleOrgExpand,
  onOpenCreateOrg,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="org-management-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business />
            {t('adminToolsPage.orgManagement.title')}
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onOpenCreateOrg}
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: BRAND_COLORS.forestGreen.main,
                opacity: 0.8,
              },
            }}
          >
            {t('adminToolsPage.orgManagement.createNewOrg')}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Unassigned Users Section */}
        {unassignedUsersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : unassignedUsers.length > 0 ? (
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <People />
              {t('adminToolsPage.orgManagement.unassignedUsers', {
                count: unassignedUsers.length,
              })}
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('adminToolsPage.orgManagement.unassignedUsersWarning')}
            </Alert>
            {unassignedUsersError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {unassignedUsersError}
              </Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('adminToolsPage.userManagement.tableHeaders.name')}</TableCell>
                    <TableCell>{t('adminToolsPage.userManagement.tableHeaders.email')}</TableCell>
                    <TableCell>
                      {t('adminToolsPage.orgManagement.tableHeaders.registered')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.orgManagement.tableHeaders.assignToOrg')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.orgManagement.tableHeaders.assignRoles')}
                    </TableCell>
                    <TableCell align="center">
                      {t('adminToolsPage.orgManagement.tableHeaders.action')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unassignedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.full_name || 'N/A'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{formatDateISO(user.created_at)}</TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <InputLabel>
                            {t('adminToolsPage.orgManagement.selectOrganization')}
                          </InputLabel>
                          <Select
                            value={selectedOrgForAssignment[user.id] || ''}
                            onChange={(e) =>
                              onSelectOrgForAssignment({
                                ...selectedOrgForAssignment,
                                [user.id]: e.target.value,
                              })
                            }
                            label={t('adminToolsPage.orgManagement.selectOrganization')}
                            disabled={assigningUserId === user.id}
                          >
                            {orgsData?.organizations.map((org: Organization) => (
                              <MenuItem key={org.id} value={org.id}>
                                {org.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormGroup row>
                          {roleDefinitions.map((role) => (
                            <Tooltip key={role.value} title={role.description} placement="top">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={(selectedRolesForAssignment[user.id] || []).includes(
                                      role.value
                                    )}
                                    onChange={() => onToggleRoleForAssignment(user.id, role.value)}
                                    disabled={assigningUserId === user.id}
                                  />
                                }
                                label={<Typography variant="caption">{role.label}</Typography>}
                                sx={{ mr: 1 }}
                              />
                            </Tooltip>
                          ))}
                        </FormGroup>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => onAssignUserToOrg(user.id)}
                          disabled={
                            !selectedOrgForAssignment[user.id] ||
                            !(selectedRolesForAssignment[user.id]?.length > 0) ||
                            assigningUserId === user.id
                          }
                          sx={{
                            backgroundColor: BRAND_COLORS.forestGreen.main,
                            '&:hover': {
                              backgroundColor: BRAND_COLORS.forestGreen.main,
                              opacity: 0.8,
                            },
                          }}
                        >
                          {assigningUserId === user.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            t('adminToolsPage.assign')
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}

        {/* Organizations Section */}
        {orgsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : orgsError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {orgsError}
          </Alert>
        ) : orgsData ? (
          <Box sx={{ mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>{t('adminToolsPage.orgManagement.tableHeaders.orgName')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgsData.organizations.map((org: Organization) => (
                    <React.Fragment key={org.id}>
                      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => onToggleOrgExpand(org.id)}>
                            {expandedOrgId === org.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {org.name}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
                          <Collapse in={expandedOrgId === org.id} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div" sx={{ mb: 2 }}>
                                {t('adminToolsPage.users')}
                              </Typography>
                              {orgUsersLoading && expandedOrgId === org.id ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : orgUsersError && expandedOrgId === org.id ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  {orgUsersError}
                                </Alert>
                              ) : orgUsersQueryData && expandedOrgId === org.id ? (
                                <>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>
                                          {t('adminToolsPage.userManagement.tableHeaders.name')}
                                        </TableCell>
                                        <TableCell>
                                          {t('adminToolsPage.userManagement.tableHeaders.email')}
                                        </TableCell>
                                        <TableCell>
                                          {t('adminToolsPage.userManagement.tableHeaders.roles')}
                                        </TableCell>
                                        <TableCell>
                                          {t('adminToolsPage.userManagement.tableHeaders.joined')}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {orgUsersQueryData.users.map((user: OrganizationUser) => (
                                        <TableRow key={user.id}>
                                          <TableCell>{user.full_name || 'N/A'}</TableCell>
                                          <TableCell>{user.email}</TableCell>
                                          <TableCell>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                gap: 0.5,
                                                flexWrap: 'wrap',
                                              }}
                                            >
                                              {user.roles && user.roles.length > 0 ? (
                                                user.roles.map((role: string) => {
                                                  const roleDef = roleDefinitions.find(
                                                    (r) => r.value === role
                                                  );
                                                  return (
                                                    <Chip
                                                      key={role}
                                                      label={roleDef?.label || role}
                                                      size="small"
                                                      color={
                                                        role === 'admin'
                                                          ? 'error'
                                                          : role === 'pilot'
                                                            ? 'primary'
                                                            : role === 'maintenance'
                                                              ? 'secondary'
                                                              : 'default'
                                                      }
                                                    />
                                                  );
                                                })
                                              ) : (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {t('adminToolsPage.roles.noRolesAssigned')}
                                                </Typography>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>{formatDateISO(user.created_at)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                  {orgUsersQueryData.users.length === 0 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 2, textAlign: 'center' }}
                                    >
                                      {t('adminToolsPage.userManagement.noUsersFound')}
                                    </Typography>
                                  )}
                                </>
                              ) : null}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {orgsData.organizations.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                {t('adminToolsPage.orgManagement.noOrgsFound')}
              </Typography>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminToolsPage.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
